import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  GetApp as DownloadIcon
} from '@mui/icons-material';
import { deviceApi, sensorDataApi, calibrationApi } from '../services/api';

interface DeviceStatus {
  id: number;
  serialNumber: string;
  mac: string;
  stationName: string;
  status: string;
  lastCalibrationDate: string;
}

interface CalibrationRecord {
  id: number;
  serialNumber: string;
  stationName: string;
  cal10Adc: number;
  cal20Adc: number;
  cal20AirAdc: number;
  slope10: number;
  slopeRatio: number;
  calibrationDate: string;
  status: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`device-tabpanel-${index}`}
      aria-labelledby={`device-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const DeviceManagementPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [devices, setDevices] = useState<DeviceStatus[]>([]);
  const [calibrations, setCalibrations] = useState<CalibrationRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // 搜尋條件
  const [searchMac, setSearchMac] = useState('');
  const [selectedStation, setSelectedStation] = useState('');
  
  // 詳情對話框
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<DeviceStatus | null>(null);

  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    setLoading(true);
    try {
      const response = await deviceApi.getAll();
      setDevices(response.data.items || []);
    } catch (err) {
      setError('載入裝置資料失敗');
    } finally {
      setLoading(false);
    }
  };

  const loadCalibrations = async () => {
    setLoading(true);
    try {
      const response = await calibrationApi.getAll();
      setCalibrations(response.data.items || []);
    } catch (err) {
      setError('載入校正記錄失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchMac.trim()) {
      setError('請輸入 MAC 地址');
      return;
    }

    setLoading(true);
    try {
      const response = await deviceApi.getRawRecords(searchMac, true, 1, 20);
      setDevices(response.data.items || []);
      setSuccess('搜尋完成');
    } catch (err) {
      setError('搜尋失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (device: DeviceStatus) => {
    setSelectedDevice(device);
    setDetailDialogOpen(true);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 1) {
      loadCalibrations();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'maintenance':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        裝置管理
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="MAC 地址搜尋"
                value={searchMac}
                onChange={(e) => setSearchMac(e.target.value)}
                placeholder="輸入 MAC 地址"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
              >
                搜尋
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadDevices}
                disabled={loading}
              >
                重新整理
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="裝置狀態" />
            <Tab label="校正記錄" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>序號</TableCell>
                  <TableCell>MAC 地址</TableCell>
                  <TableCell>工作站</TableCell>
                  <TableCell>狀態</TableCell>
                  <TableCell>最後校正時間</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.map((device) => (
                  <TableRow key={device.id}>
                    <TableCell>{device.serialNumber}</TableCell>
                    <TableCell>{device.mac}</TableCell>
                    <TableCell>{device.stationName}</TableCell>
                    <TableCell>
                      <Chip
                        label={device.status}
                        color={getStatusColor(device.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {device.lastCalibrationDate 
                        ? new Date(device.lastCalibrationDate).toLocaleString()
                        : '無'
                      }
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleViewDetails(device)}>
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton>
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {devices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      暫無資料
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>序號</TableCell>
                  <TableCell>工作站</TableCell>
                  <TableCell>Cal10 ADC</TableCell>
                  <TableCell>Cal20 ADC</TableCell>
                  <TableCell>Slope10</TableCell>
                  <TableCell>校正時間</TableCell>
                  <TableCell>狀態</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {calibrations.map((calibration) => (
                  <TableRow key={calibration.id}>
                    <TableCell>{calibration.serialNumber}</TableCell>
                    <TableCell>{calibration.stationName}</TableCell>
                    <TableCell>{calibration.cal10Adc}</TableCell>
                    <TableCell>{calibration.cal20Adc}</TableCell>
                    <TableCell>{calibration.slope10}</TableCell>
                    <TableCell>
                      {new Date(calibration.calibrationDate).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={calibration.status}
                        color={getStatusColor(calibration.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton>
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton>
                        <DownloadIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {calibrations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      暫無資料
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Card>

      {/* 裝置詳情對話框 */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>裝置詳情</DialogTitle>
        <DialogContent>
          {selectedDevice && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">序號</Typography>
                  <Typography>{selectedDevice.serialNumber}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">MAC 地址</Typography>
                  <Typography>{selectedDevice.mac}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">工作站</Typography>
                  <Typography>{selectedDevice.stationName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">狀態</Typography>
                  <Chip
                    label={selectedDevice.status}
                    color={getStatusColor(selectedDevice.status) as any}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">最後校正時間</Typography>
                  <Typography>
                    {selectedDevice.lastCalibrationDate 
                      ? new Date(selectedDevice.lastCalibrationDate).toLocaleString()
                      : '無'
                    }
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>關閉</Button>
        </DialogActions>
      </Dialog>

      {/* 通知 */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeviceManagementPage;
